import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    analyticsCardPinwheelFailureMVP2ClickForm,
    analyticsCardPinwheelFailureMVP2ClickInstructions,
    analyticsCardPinwheelFailureMVP2ClickDone,
    analyticsCardPinwheelFailureMVP2ClickTryAgain,
    analyticsCardPinwheelFailureMVP2PageView,
} from '@perpay-web/fintech/actions/analytics/cardOnboardingMVP2';
import CardPinwheelSetupFailure from '@perpay-web/fintech/components/composite/CardPinwheelSetupFailure/CardPinwheelSetupFailure';
import {
    cardManualPaymentSetupDataModule,
    getAccountNumber,
    getRoutingNumber,
    getMinimumPayment,
} from '@perpay-web/fintech/dataModules/composed/cardManualPaymentSetup';
import {
    fetchPayrollInstructionsDataModule,
    getDirectDepositFormUrl,
} from '@perpay-web/fintech/dataModules/fetchPayrollInstructions';
import { getAmountToCharge } from '@perpay-web/fintech/selectors/ui/cardOnboardingMVP2';

/**
 * Container component for CardPinwheelSetupFailure
 * @param {Object} props - Component props
 * @param {Function} [props.goBackToPinwheel] - Callback to go back to Pinwheel
 * @returns {JSX.Element} CardPinwheelSetupFailure component
 */
const CardPinwheelSetupFailureContainer = ({ goBackToPinwheel, isPHO }) => {
    const dispatch = useDispatch();

    const isLoading = useSelector(
        cardManualPaymentSetupDataModule.getIsLoadingOrUnrequested,
    );
    const minimumPayment = useSelector(getMinimumPayment);
    const accountNumber = useSelector(getAccountNumber);
    const routingNumber = useSelector(getRoutingNumber);
    const directDepositFormUrl = useSelector(getDirectDepositFormUrl);
    const amountToCharge = useSelector(getAmountToCharge);

    const fetchPaymentSetupData = useCallback(
        (amount) => {
            dispatch(
                cardManualPaymentSetupDataModule.dataRequest({
                    actions: [
                        fetchPayrollInstructionsDataModule.dataRequest({
                            amount,
                        }),
                    ],
                }),
            );
        },
        [dispatch],
    );

    const onMount = useCallback(() => {
        fetchPaymentSetupData(amountToCharge);
        dispatch(analyticsCardPinwheelFailureMVP2PageView());
    }, [dispatch, fetchPaymentSetupData, amountToCharge]);

    const onModalClose = useCallback(() => {
        // TODO make better
        // we need this function to re-fetch the payroll instructions since when
        // the modal is closed, the fetchPayrollInstructionsDataModule is reset
        // otherwise there's an infinite loader situation
        fetchPaymentSetupData(amountToCharge);
        dispatch(analyticsCardPinwheelFailureMVP2PageView());
    }, [dispatch, fetchPaymentSetupData, amountToCharge]);

    const onClickTryAgain = useCallback(() => {
        goBackToPinwheel();
        dispatch(analyticsCardPinwheelFailureMVP2ClickTryAgain());
    }, [dispatch, goBackToPinwheel]);

    const onClickDone = useCallback(
        () => dispatch(analyticsCardPinwheelFailureMVP2ClickDone()),
        [dispatch],
    );

    const onClickForm = useCallback(
        () => dispatch(analyticsCardPinwheelFailureMVP2ClickForm()),
        [dispatch],
    );

    const onClickInstructions = useCallback(
        () => dispatch(analyticsCardPinwheelFailureMVP2ClickInstructions()),
        [dispatch],
    );

    const onUnmount = useCallback(() => {
        dispatch(cardManualPaymentSetupDataModule.dataReset());
    }, [dispatch]);

    return (
        <CardPinwheelSetupFailure
            isLoading={isLoading}
            minimumPayment={minimumPayment}
            amountToCharge={amountToCharge}
            accountNumber={accountNumber}
            routingNumber={routingNumber}
            directDepositFormUrl={directDepositFormUrl}
            onMount={onMount}
            onModalClose={onModalClose}
            onClickTryAgain={onClickTryAgain}
            onClickDone={onClickDone}
            onClickForm={onClickForm}
            onClickInstructions={onClickInstructions}
            onUnmount={onUnmount}
            isPHO={isPHO}
        />
    );
};

export default CardPinwheelSetupFailureContainer;
